/**
 * `/documents` search params: `?type=<kind>&q=<text>&open=<documentId>`.
 * Every key is optional so links to the library need none of them.
 */
import { DOCUMENT_KINDS, type DocumentKind } from '@outsource/shared/schemas/documents';

export const ALL_TYPES = 'all';
export type TypeFilter = DocumentKind | typeof ALL_TYPES;

export interface DocumentsSearch {
  readonly type?: DocumentKind;
  readonly q?: string;
  readonly open?: string;
}

const MAX_QUERY_LENGTH = 200;
const DOCUMENT_ID_RE = /^[A-Za-z0-9_-]{1,64}$/;

const isDocumentKind = (value: unknown): value is DocumentKind =>
  typeof value === 'string' && (DOCUMENT_KINDS as readonly string[]).includes(value);

/** The router parses `?q=2026` as a number; the search box always means text. */
function asQuery(value: unknown): string | undefined {
  const text = typeof value === 'number' ? String(value) : value;
  if (typeof text !== 'string' || text.trim() === '') return undefined;
  return text.slice(0, MAX_QUERY_LENGTH);
}

const asDocumentId = (value: unknown): string | undefined =>
  typeof value === 'string' && DOCUMENT_ID_RE.test(value) ? value : undefined;

/** Route `validateSearch`: unknown kinds, blank searches and malformed ids are dropped. */
export function validateDocumentsSearch(input: Record<string, unknown>): DocumentsSearch {
  const q = asQuery(input.q);
  const open = asDocumentId(input.open);
  return {
    ...(isDocumentKind(input.type) ? { type: input.type } : {}),
    ...(q !== undefined ? { q } : {}),
    ...(open !== undefined ? { open } : {}),
  };
}

/** Design "Type" options (Documents.dc.html TYPES), in order. */
export const DESIGN_KINDS: readonly DocumentKind[] = [
  'contract',
  'amendment',
  'nda',
  'invoice_pdf',
  'statement',
  'other',
];

const CLIENT_INVOICE: DocumentKind = 'client_invoice';
const AFTER_INVOICE_PDF = DESIGN_KINDS.indexOf('invoice_pdf') + 1;

/**
 * Kinds offered by the Type filter and the Type select: the design's six, plus "Client invoice"
 * (after "Invoice PDF") only while the library holds such files or one is selected.
 */
export function offeredKinds(clientInvoiceCount: number, current?: DocumentKind): readonly DocumentKind[] {
  if (clientInvoiceCount === 0 && current !== CLIENT_INVOICE) return DESIGN_KINDS;
  return [
    ...DESIGN_KINDS.slice(0, AFTER_INVOICE_PDF),
    CLIENT_INVOICE,
    ...DESIGN_KINDS.slice(AFTER_INVOICE_PDF),
  ];
}
